import React from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Field, OptionButton, keepVisible } from '@/components/booking/fields';
import { Row } from '@/components/booking/landSteps';
import { formatPhone } from '@/pages/Register';
import { QUOTE_SERVICES, TIMELINES, BUDGETS, QUOTE_LABELS } from '@/lib/bookFlow';

const textareaCls = 'min-h-[120px] text-base bg-[#0f1724] border-white/15 text-white placeholder:text-white/30 focus:border-[#C6A14A]';
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const Options = ({ list, value, onPick, prefix }) => (
  <div className="space-y-2" role="radiogroup">
    {list.map((o) => (
      <OptionButton key={o} selected={value === o} onClick={() => onPick(o)} testId={`${prefix}-${slug(o)}`}>{o}</OptionButton>
    ))}
  </div>
);

export const buildQuoteSteps = ({ kind, form, set, ref }) => [
  {
    key: 'service', title: kind === 'video' ? 'What kind of video?' : 'Which service do you need?',
    validate: () => (form.service ? null : 'Please choose a service'),
    body: <Options list={QUOTE_SERVICES[kind]} value={form.service} onPick={(v) => set('service', v)} prefix="book-service" />,
  },
  {
    key: 'where', title: 'Where is it?', hint: 'City, or the full address if you have it.',
    validate: () => (form.location.trim().length >= 2 ? null : 'Please enter the city or address'),
    body: <Field ref={ref} id="quote-location" label="Location" required autoComplete="address-level2" placeholder="Kingman, AZ" value={form.location} onChange={(e) => set('location', e.target.value)} />,
  },
  {
    key: 'timeline', title: 'When do you need it?',
    validate: () => (form.timeline ? null : 'Please choose a timeline'),
    body: <Options list={TIMELINES} value={form.timeline} onPick={(v) => set('timeline', v)} prefix="book-timeline" />,
  },
  {
    key: 'description', title: 'Tell us about the project', hint: 'A sentence or two is plenty.',
    validate: () => (form.description.trim().length >= 10 ? null : 'Please add a short description (at least 10 characters)'),
    body: (
      <div className="space-y-1.5">
        <Label htmlFor="quote-description" className="text-[#E8E4DE]/80 text-sm">Description<span className="text-[#C6A14A]"> *</span></Label>
        <Textarea ref={ref} id="quote-description" onFocus={keepVisible} placeholder="e.g. 60-second tour of a 4-bed listing, ready by Friday" value={form.description} onChange={(e) => set('description', e.target.value)} className={textareaCls} data-testid="book-quote-description" />
      </div>
    ),
  },
  {
    key: 'budget', title: 'Do you have a budget in mind?', hint: 'Optional — helps us suggest the right option.',
    validate: () => null,
    body: <Options list={BUDGETS} value={form.budget} onPick={(v) => set('budget', v)} prefix="book-budget" />,
  },
];

export const buildContactStep = ({ contact, setContact, ref }) => ({
  key: 'contact', title: 'Who should we contact?',
  validate: () => (contact.contactName.trim().length < 2 ? 'Please enter a contact name'
    : contact.contactPhone.replace(/\D/g, '').length !== 10 ? 'Please enter a 10-digit phone number' : null),
  body: (
    <>
      <Field ref={ref} id="contact-name" label="Contact name" required autoComplete="name" value={contact.contactName} onChange={(e) => setContact('contactName', e.target.value)} />
      <Field id="contact-phone" label="Contact phone" required type="tel" inputMode="tel" autoComplete="tel" placeholder="(928) 555-1234" value={contact.contactPhone} onChange={(e) => setContact('contactPhone', formatPhone(e.target.value))} />
    </>
  ),
});

export const toQuoteForm = (kind, draft) => (kind === 'commercial'
  ? { service: 'Commercial real estate photography', location: draft.commercial.address, description: draft.commercial.description, timeline: '', budget: '' }
  : draft[kind] || {});

export const buildCommercialSteps = ({ form, set, ref }) => [
  {
    key: 'address', title: "What's the property address?", hint: 'Street, city and ZIP.',
    validate: () => (form.address.trim().length >= 5 ? null : 'Please enter the property address'),
    body: <Field ref={ref} id="commercial-address" label="Property address" required autoComplete="street-address" placeholder="123 Beale St, Kingman, AZ 86401" value={form.address} onChange={(e) => set('address', e.target.value)} />,
  },
  {
    key: 'description', title: 'Tell us about the property and shoot', hint: 'Type of property, size, what you need and when.',
    validate: () => (form.description.trim().length >= 10 ? null : 'Please add a short description (at least 10 characters)'),
    body: (
      <div className="space-y-1.5">
        <Label htmlFor="commercial-description" className="text-[#E8E4DE]/80 text-sm">Description<span className="text-[#C6A14A]"> *</span></Label>
        <Textarea ref={ref} id="commercial-description" onFocus={keepVisible} placeholder="e.g. 12,000 sq ft retail plaza, exterior + interior photos and drone, needed by next Friday" value={form.description} onChange={(e) => set('description', e.target.value)} className={textareaCls} data-testid="book-commercial-description" />
      </div>
    ),
  },
];

export const QuoteSummary = ({ kind, form, contact }) => (
  <>
    <div className="rounded-xl border border-white/10 bg-[#0f1724] px-4" data-testid="quote-summary">
      <Row k="Type" v={QUOTE_LABELS[kind]} />
      {kind !== 'commercial' && <Row k="Service" v={form.service} />}
      <Row k={kind === 'commercial' ? 'Address' : 'Location'} v={form.location} />
      {form.timeline && <Row k="Timeline" v={form.timeline} />}
      <Row k="Project" v={form.description} />
      {form.budget && <Row k="Budget" v={form.budget} />}
      <Row k="Contact" v={[contact.contactName, contact.contactPhone].filter(Boolean).join(' · ')} />
    </div>
    <p className="text-sm text-[#E8E4DE]/60">No payment now — we'll reply with a price within 24 hours.</p>
  </>
);
